if (!customElements.get('image-gallery')) {
  customElements.define(
    'image-gallery',
    class ImageGallery extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
        this.dialog = this.querySelector('[data-gallery-dialog]');
        this.stage = this.querySelector('.image-gallery__lightbox-stage');
        this.viewport = this.querySelector('[data-gallery-viewport]');
        this.lightboxImage = this.querySelector('[data-gallery-image]');
        this.lightboxCaption = this.querySelector('[data-gallery-lightbox-caption]');
        this.counter = this.querySelector('[data-gallery-counter]');
        this.previousButton = this.querySelector('[data-gallery-previous]');
        this.nextButton = this.querySelector('[data-gallery-next]');
        this.closeButton = this.querySelector('[data-gallery-close]');
        this.fullscreenButton = this.querySelector('[data-gallery-fullscreen]');

        if (
          !this.items.length ||
          !this.dialog ||
          !this.stage ||
          !this.viewport ||
          !this.lightboxImage ||
          !this.lightboxCaption
        ) {
          return;
        }

        this.currentIndex = 0;
        this.lastTrigger = null;
        this.isNativeSize = false;

        this.items.forEach((item, index) => {
          item.addEventListener('click', () => this.open(index));
        });

        this.previousButton?.addEventListener('click', () => this.show(this.currentIndex - 1));
        this.nextButton?.addEventListener('click', () => this.show(this.currentIndex + 1));
        this.closeButton?.addEventListener('click', () => this.close());
        this.fullscreenButton?.addEventListener('click', () => this.toggleNativeSize());

        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });

        this.dialog.addEventListener('close', () => this.afterClose());

        this.handleKeydown = (event) => {
          if (!this.dialog.open) return;

          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.show(this.currentIndex - 1);
          }

          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.show(this.currentIndex + 1);
          }

          if (event.key === 'Home') {
            event.preventDefault();
            this.show(0);
          }

          if (event.key === 'End') {
            event.preventDefault();
            this.show(this.items.length - 1);
          }
        };

        document.addEventListener('keydown', this.handleKeydown);

        const hasMultipleImages = this.items.length > 1;
        this.previousButton?.toggleAttribute('hidden', !hasMultipleImages);
        this.nextButton?.toggleAttribute('hidden', !hasMultipleImages);
        this.updateSizeButton();
      }

      disconnectedCallback() {
        if (this.handleKeydown) {
          document.removeEventListener('keydown', this.handleKeydown);
        }

        document.body.classList.remove('image-gallery-lightbox-open');
      }

      open(index) {
        this.lastTrigger = this.items[index];
        this.setNativeSize(false);
        this.show(index);

        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        document.body.classList.add('image-gallery-lightbox-open');
        this.closeButton?.focus();
      }

      close() {
        if (typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
          this.afterClose();
        }
      }

      afterClose() {
        document.body.classList.remove('image-gallery-lightbox-open');
        this.setNativeSize(false);
        this.lightboxImage.removeAttribute('src');
        this.lastTrigger?.focus();
      }

      show(index) {
        const total = this.items.length;
        this.currentIndex = (index + total) % total;
        const item = this.items[this.currentIndex];

        this.lightboxImage.width = Number(item.dataset.galleryWidth) || 1;
        this.lightboxImage.height = Number(item.dataset.galleryHeight) || 1;
        this.lightboxImage.alt = item.dataset.galleryAlt || '';
        this.lightboxCaption.textContent = item.dataset.galleryCaption || '';
        this.counter.textContent = `${this.currentIndex + 1} / ${total}`;
        this.lightboxImage.src = item.dataset.gallerySrc;

        this.viewport.scrollTo({ top: 0, left: 0 });
      }

      toggleNativeSize() {
        this.setNativeSize(!this.isNativeSize);
      }

      setNativeSize(enabled) {
        this.isNativeSize = Boolean(enabled);
        this.dialog.classList.toggle('image-gallery__lightbox--native', this.isNativeSize);
        this.viewport.scrollTo({ top: 0, left: 0 });
        this.updateSizeButton();
      }

      updateSizeButton() {
        if (!this.fullscreenButton) return;

        this.fullscreenButton.setAttribute('aria-pressed', String(this.isNativeSize));
        this.fullscreenButton.setAttribute(
          'aria-label',
          this.isNativeSize ? 'Fit image to window' : 'View image at full size'
        );
      }
    }
  );
}
